import React, { FC } from 'react';

import { Button, Form, Layout, message, Row, Space, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';

import Api from '@app/services/api';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { PageTitle } from '@components/PageTitle';

import { Assignments } from '../../component/Assignments';
import { useEmployee } from '../../providers';

import { UserForm } from './components/UserForm';
import { IFormValues } from './interfaces';

import { SpinnerContainer } from './styles';

export const EditEmployee: FC = () => {
  const params = useParams<{ id: string }>();
  const [submitting, setSubmitting] = React.useState(false);
  const commonT = useTranslation(NSKeys.common);
  const employeesT = useTranslation(NSKeys.employees);
  const navigate = useNavigate();
  const [form] = Form.useForm<IFormValues>();
  const employee = useEmployee();
  const validationT = useTranslation(NSKeys.validation);
  const currentUser = useLocalSelector((state) => state.currentSession.data);
  // Профиль и раздел назначений гейтятся серверными объектными правами.
  const canUpdate = !!employee.data?.$permissions?.update;
  const canViewAssignments = currentUser?.scope?.$permissions?.view_assignments === true;

  const goBack = () => navigate('/dashboard/employees');

  const handleSubmit = async (formValues: IFormValues): Promise<void> => {
    if (employee.data) {
      setSubmitting(true);
      // Трёхзначность tag_uids: кладём поле в тело ТОЛЬКО если набор тегов реально изменился,
      // иначе опускаем — чтобы не затереть теги при редактировании других полей.
      const initialTagUids = (employee.data.tags || []).map((tag) => tag.uid);
      const { tag_uids, ...rest } = formValues;
      const tagsChanged =
        !!tag_uids &&
        (tag_uids.length !== initialTagUids.length ||
          [...tag_uids].sort().join('|') !== [...initialTagUids].sort().join('|'));

      await Api.users
        .putUser(params?.id || '', {
          settings: { ...employee.data.settings },
          ...rest,
          ...(tagsChanged ? { tag_uids } : {}),
        })
        .then(() => {
          message.success(employeesT.t('form.toastEditSuccess'));
          goBack();
        })
        .catch((err) => {
          const { pointerErrors } = getFormErrorsFromResponse(formValues, err, validationT.t);
          if (pointerErrors.length) {
            form.setFields(pointerErrors);
          } else {
            message.error(commonT.t('error400'));
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  return (
    <>
      <Layout.Header>
        <Breadcrumbs />
        <Row justify="space-between" align="middle">
          <PageTitle title={employeesT.t('titleEdit')} onClick={() => navigate('/dashboard/employees')} />
          <Space size="middle">
            <Button disabled={submitting} type="primary" onClick={() => form.submit()} size="middle">
              {commonT.t('buttonSave')}
            </Button>
            <Button onClick={goBack} size="middle">
              {commonT.t('buttonCancel')}
            </Button>
          </Space>
        </Row>
      </Layout.Header>
      {!employee.data || employee.loading ? (
        <SpinnerContainer>
          <Spin />
        </SpinnerContainer>
      ) : (
        <>
          <UserForm
            form={form}
            onFinish={handleSubmit}
            disabled={!canUpdate}
            initialValues={{
              first_name: employee.data?.first_name,
              email: employee.data?.email,
              last_name: employee.data?.last_name,
              phone: employee.data?.phone || '',
              title: employee.data?.title,
              is_active: employee.data?.is_active,
              tag_uids: (employee.data?.tags || []).map((tag) => tag.uid),
            }}
          />
          {canViewAssignments && <Assignments employee={employee.data} />}
        </>
      )}
    </>
  );
};
